// src/pages/orders/components/DeleteFlagDialog.tsx
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import FlagIcon from "@mui/icons-material/Flag";
import { Flag } from "../types";
import { useTranslation } from "../../../../hooks/useTranslation";

type Props = {
  open: boolean;
  onClose: () => void;
  flag: Flag;
};

export const DeleteFlag: React.FC<Props> = ({ open, onClose, flag }) => {
  const { translate, translateWithoutPrefix } =
    useTranslation("pages.orders.flag");

  const invalidate = useInvalidate();
  const { mutate, isLoading } = useCustomMutation();

  const handleDelete = () => {
    if (!flag) return;

    mutate(
      {
        url: `store/user-flags/${flag.id}`,
        method: "delete",
        values: {},
        successNotification: () => ({
          message: translate("successDelete"),
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });
          onClose();
        },
      },
    );
  };

  if (!flag) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Box display="flex" flexDirection="row" alignItems="center" mb={2}>
          <FlagIcon fontSize="large" sx={{ color: flag.flagColor, mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            {flag.flagName}
          </Typography>
        </Box>
        <Typography variant="body1">
          {translate("deleteConfirmation")}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {translateWithoutPrefix("buttons.cancel")}
        </Button>
        <LoadingButton
          onClick={handleDelete}
          color="error"
          variant="contained"
          loading={isLoading}
        >
          {translateWithoutPrefix("buttons.delete")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
